import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const ShippingReturns = () => {
  const { t } = useLanguage();
  
  return (
    <div className="pt-24 pb-16" data-testid="shipping-returns-page">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        {/* Back to Cart Link */}
        <Link 
          to="/cart" 
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
          data-testid="back-to-cart-link"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('backToCart')}
        </Link>

        {/* Header */}
        <div className="text-center mb-12">
          <p className="label-style mb-2">{t('deliveryInformation')}</p>
          <h1 
            className="text-4xl md:text-5xl font-bold mb-4"
            style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
          >
            {t('shippingReturns')}
          </h1>
        </div>

        <div className="space-y-8">
          {/* Important Notice */}
          <div className="bg-yellow-50 border border-yellow-200 p-6 rounded-lg">
            <h2 
              className="font-bold text-xl mb-4"
              style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
            >
              {t('importantMadeToOrder')}
            </h2>
            <p className="text-muted-foreground mb-4">
              {t('madeToOrderNotice')}
            </p>
            <p className="text-muted-foreground mb-4">
              <strong>{t('currentDispatchTime')}</strong> {t('subjectToChange')}
            </p>
            <p className="text-muted-foreground mb-4">
              eg. Royal Mail 48 Tracked - this is a 48-72 hour delivery from Royal Mail, however all items are made to order and will need a manufacture period, which will be stated at checkout.
            </p>
            <p className="text-muted-foreground mb-2">
              {t('subjectToChange')}
            </p>
            <p className="text-muted-foreground font-medium">
              {t('pleaseBareinMind')}
            </p>
          </div>

          {/* Delivery Terms */}
          <div className="bg-white border border-border p-6 rounded-lg">
            <p className="text-muted-foreground">
              Zen Graphics Limited will endeavour to meet any delivery date quoted but delivery dates and times given are intended as an estimate only. If there are any circumstances whatsoever which prevent or delay delivery, Zen Graphics Limited shall not be bound to supply or make delivery of any goods ordered nor shall it be liable for any damage or consequential loss or in any other way for failure to supply or delay in delivery when so prevented or delayed.
            </p>
          </div>

          {/* Shipping Options by Region */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* USA */}
            <div className="bg-white border border-border p-6 rounded-lg">
              <h3 
                className="font-bold text-lg mb-4"
                style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
              >
                {t('usa')}
              </h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• USPS Priority Tracked</li>
                <li>• UPS Express delivery</li>
              </ul>
            </div>

            {/* UK */}
            <div className="bg-white border border-border p-6 rounded-lg">
              <h3 
                className="font-bold text-lg mb-4"
                style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
              >
                {t('uk')}
              </h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• Royal Mail 48 Tracked</li>
                <li>• Royal Mail 24 Tracked</li>
              </ul>
              <p className="text-sm text-muted-foreground mt-4">
                Where your item is too large for Royal Mail Postage, a courier will be used from either UPS, Parcelforce or Hermes.
              </p>
            </div>

            {/* Europe */}
            <div className="bg-white border border-border p-6 rounded-lg">
              <h3 
                className="font-bold text-lg mb-4"
                style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
              >
                {t('europe')}
              </h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• Tracked Europe Shipping - 5-14 day delivery</li>
                <li>• UPS EU Zone 1 - 4-7 day delivery</li>
                <li>• UPS EU Zone 2 - 5-8 day delivery</li>
              </ul>
            </div>

            {/* Worldwide */}
            <div className="bg-white border border-border p-6 rounded-lg">
              <h3 
                className="font-bold text-lg mb-4"
                style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
              >
                {t('worldwide')}
              </h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• Priority post with tracking</li>
                <li>• 5-14 day delivery Worldwide</li>
              </ul>
              <p className="text-sm text-muted-foreground mt-4">
                Royal Mail uses delivery partners in the receiving country. For more information:{' '}
                <a 
                  href="https://www.royalmail.com/sending/international/international-tracked-signed/delivery-partners" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  Royal Mail Delivery Partners
                </a>
              </p>
            </div>
          </div>

          {/* Shipping Calculation */}
          <div className="bg-secondary/30 p-6 rounded-lg">
            <h3 
              className="font-bold text-lg mb-4"
              style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
            >
              {t('shippingCalculation')}
            </h3>
            <p className="text-muted-foreground">
              Shipping is calculated in the checkout process. All services available for the item/s you wish to purchase will be shown during checkout. Discount codes may not apply to shipping costs.
            </p>
          </div>

          {/* UPS Extended Areas */}
          <div className="bg-white border border-border p-6 rounded-lg">
            <h3 
              className="font-bold text-lg mb-4"
              style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
            >
              {t('upsExtendedAreas')}
            </h3>
            <p className="text-muted-foreground mb-4">
              Some areas accrue a rural delivery charge, this includes the Scottish Highlands. Some areas of Europe also fall into this extended area delivery charge and we may cancel or change the delivery method at our discretion based on your location.
            </p>
            <p className="text-muted-foreground mb-4">
              We are working to factor this area based charge into our pricing structure, however we do not have this facility at this time. If you would like to know if your area is within UPS extended delivery areas please see:{' '}
              <a 
                href="https://www.ups.com/gb/en/shipping/zones-and-rates/area-surcharge.page" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                UPS Area Surcharges
              </a>
            </p>
            <p className="text-muted-foreground">
              Orders for bulkier items may be sent via courier at our discretion. We use Hermes, DPD, TNT, UPS and Parcelforce services. If your order is to be dispatched via this method you will receive an email from the courier.
            </p>
          </div>

          {/* Returns Policy */}
          <div className="bg-white border border-border p-6 rounded-lg">
            <h2 
              className="font-bold text-xl mb-4"
              style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
            >
              {t('returnsPolicy')}
            </h2>
            <p className="text-muted-foreground mb-4">
              Returns are accepted if reported within <strong>30 days</strong> of receipt of the item.
            </p>
            <p className="text-muted-foreground mb-4">
              For items customised by size or colour, there is a <strong>30% restocking fee</strong> on the returned item.
            </p>
            <p className="text-muted-foreground">
              For your own peace of mind, we recommend using a tracked or signed for service.
            </p>
          </div>

          {/* Returns Address */}
          <div className="bg-primary/5 border border-primary/20 p-6 rounded-lg">
            <h3 
              className="font-bold text-lg mb-4"
              style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
            >
              {t('returnsAddress')}
            </h3>
            <p className="text-muted-foreground mb-4">Please send all returns to:</p>
            <address className="text-muted-foreground not-italic leading-relaxed">
              <strong>Zen Graphics Ltd</strong><br />
              Unit 11 Apollo Park<br />
              Apollo<br />
              Tamworth<br />
              B79 7TA<br />
              United Kingdom
            </address>
            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded">
              <p className="text-sm text-muted-foreground">
                <strong>Important:</strong> All returns should be carefully packaged and a letter should be enclosed with your name and order number attached for processing. Returns without this information will be delayed or may not be processed if the origins cannot be found.
              </p>
            </div>
          </div>

          {/* Contact for Questions */}
          <div className="text-center bg-secondary/30 p-6 rounded-lg">
            <h3 
              className="font-bold text-lg mb-2"
              style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
            >
              {t('questionsTitle')}
            </h3>
            <p className="text-muted-foreground mb-4">
              If you have any questions about shipping or returns, please contact us.
            </p>
            <a 
              href="mailto:ministripesuk@gmail.com" 
              className="text-primary hover:underline"
            >
              ministripesuk@gmail.com
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShippingReturns;
